import React, { useEffect, useRef, useContext, useMemo } from 'react';
import { Modal } from '../Modal';
import { LOGGER_TYPES } from '../../LoggerFactory/loggerTypes';
import {
  logNavigationClickFromLink,
  searchResultsAction,
} from '../../LoggerFactory/BiAnalyticsLogger/events';
import { LoggerContext } from '../../appContext/LoggerProvider';
import {
  NAV_TYPES,
  SEARCH_USER_ACTIONS,
} from '../../LoggerFactory/BiAnalyticsLogger/events/consts';
import { StoreContext } from '../../appContext/StoreProvider';
import {
  fromRelativeUrlToAbsoluteUrl,
  getArticleNameAndCategoryFromArticleLink,
} from '../../../utils/locationUtils';
import window from 'global';
import { httpClient } from '../../../common/utils/httpClient';
import debounce from 'lodash/debounce';
import { SearchBox } from './SearchBox';
import { ViewerWrapper } from '../../../Viewer/ViewerWrapper';
import { IResult } from './SearchBox/SearchResults/Result';

export const NewSearchContainer = ({
  isOpen,
  closeModal,
  viewer,
}: {
  isOpen: boolean;
  closeModal: () => void;
  viewer: ViewerWrapper;
}) => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [searchResults, setSearchResults] = React.useState<IResult[]>([]);
  const [isFetchingResults, setIsFetchingResults] = React.useState(false);
  const { store } = useContext(StoreContext);
  const { logger } = useContext(LoggerContext);

  const relatedSitesMap: Map<string, string> = useMemo(
    () => getRelatedSitesMap(),
    [viewer.viewerConfig.relatedSites],
  );

  const debounceSearch = useRef(debounce(fetchResults, 500, { leading: true }))
    .current;

  useEffect(() => {
    if (shouldFetchResults(searchTerm)) {
      setIsFetchingResults(true); // We want to show the loader once the user start typing in order and not after the debounce.
    }
    debounceSearch(searchTerm);
  }, [searchTerm]);

  return (
    <Modal
      dataHook="search-modal"
      isOpen={isOpen}
      closeModal={() => {
        onCancelSearch();
        exitModal();
      }}
    >
      <SearchBox
        closeModal={exitModal}
        sendBiEvents={sendBiEvents}
        searchResults={searchResults}
        setSearchTerm={setSearchTerm}
        searchTerm={searchTerm}
        isFetchingResults={isFetchingResults}
        relatedSitesMap={relatedSitesMap}
      />
    </Modal>
  );

  async function fetchResults(query: string) {
    try {
      if (shouldFetchResults(query)) {
        const { data } = await httpClient.post<IResult[]>(
          `/api/v1/wix-docs-search`,
          {
            viewerId: store.viewer.id,
            query: query.trim(),
          },
        );

        setSearchResults(data);
      } else {
        setSearchResults([]);
      }
    } catch (e) {
      console.error(`Search failed for query: ${query.trim()}, error:`, e);
      window.Sentry.captureException(e);
      setSearchResults([]);
    } finally {
      setIsFetchingResults(false);
    }
  }

  function shouldFetchResults(_searchTerm) {
    return _searchTerm && _searchTerm.length > 1;
  }

  function getRelatedSitesMap(): Map<string, string> {
    const _relatedSitesMap = new Map();
    viewer.viewerConfig.relatedSites.forEach((site) => {
      _relatedSitesMap.set(site.viewerId, site.name);
    });
    return _relatedSitesMap;
  }

  function exitModal() {
    setSearchTerm('');
    closeModal();
  }

  function onCancelSearch() {
    sendSearchBiEvent(SEARCH_USER_ACTIONS.CANCEL);
  }

  function sendBiEvents(userAction, chosenArticleLink) {
    sendSearchBiEvent(userAction, chosenArticleLink);
    if (userAction === SEARCH_USER_ACTIONS.RESULT_CLICK) {
      sendNavigationBiEvent(chosenArticleLink);
    }
  }

  function sendSearchBiEvent(userAction, chosenArticleLink = undefined) {
    const firstSearchResult =
      searchResults[0] &&
      getArticleNameAndCategoryFromArticleLink(searchResults[0].link);
    const chosenSearchResult =
      chosenArticleLink &&
      getArticleNameAndCategoryFromArticleLink(chosenArticleLink);
    logger.getLogger(LOGGER_TYPES.ANALYTICS_BI).log(searchResultsAction, {
      first_article_name: firstSearchResult && firstSearchResult.articleName,
      first_article_category:
        firstSearchResult && firstSearchResult.categoryName,
      first_article_url:
        searchResults[0] &&
        fromRelativeUrlToAbsoluteUrl(
          searchResults[0].link,
          window.location.origin,
          store,
        ),
      search_term: searchTerm,
      user_action: userAction,
      chosen_article_url:
        chosenArticleLink &&
        fromRelativeUrlToAbsoluteUrl(
          chosenArticleLink,
          window.location.origin,
          store,
        ),
      chosen_article_name: chosenSearchResult && chosenSearchResult.articleName,
      chosen_article_category:
        chosenSearchResult && chosenSearchResult.categoryName,
      num_of_results: searchResults.length,
    });
  }

  function sendNavigationBiEvent(chosenArticleLink) {
    if (chosenArticleLink) {
      logNavigationClickFromLink(
        logger,
        chosenArticleLink,
        store,
        NAV_TYPES.SEARCH_RESULT,
      );
    }
  }
};
